import { Button, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDownload, IconUpload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { DeleteButton } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { useMobilityCertificateDelete, useMobilityCertificateDownload } from '../../model/MobilityQuery';
import { Mobility } from '../../model/MobilityTypes';
import { MobilityCertificateUploadModal } from './MobilityCertificateUploadModal';

interface MobilityCertificateProps {
  mobility: Mobility;
}

export function MobilityCertificate({ mobility }: MobilityCertificateProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [uploadOpened, { open: openUpload, close: closeUpload }] = useDisclosure(false);
  const download = useMobilityCertificateDownload(mobility.id);
  const deleteCertificate = useMobilityCertificateDelete(mobility.id);

  const { certificate } = mobility;

  const onDownload = () =>
    download.mutate(certificate?.title ?? 'certificate', {
      onError: notification.error,
    });

  const onDelete = () =>
    deleteCertificate
      .mutateAsync(undefined, {
        onError: notification.error,
        onSuccess: notification.success,
      })
      // Already reported through onError; DeleteButton only awaits the settle to close its dialog.
      .catch(() => undefined);

  return (
    <Group
      gap="xs"
      align="center"
    >
      <Text
        size="sm"
        c={certificate == null ? 'dimmed' : undefined}
        fs={certificate == null ? 'italic' : undefined}
      >
        {t('mobilities.certificate.title')}: {certificate?.title ?? t('mobilities.certificate.missing')}
      </Text>
      {certificate != null && (
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconDownload size={14} />}
          onClick={onDownload}
          loading={download.isPending}
        >
          {t('mobilities.certificate.download')}
        </Button>
      )}
      <Button
        size="xs"
        variant="outline"
        leftSection={<IconUpload size={14} />}
        onClick={openUpload}
      >
        {t(certificate == null ? 'mobilities.certificate.upload' : 'mobilities.certificate.replace')}
      </Button>
      {certificate != null && (
        <DeleteButton
          size="xs"
          onDelete={onDelete}
          isDeleting={deleteCertificate.isPending}
        />
      )}
      {uploadOpened && (
        <MobilityCertificateUploadModal
          mobilityId={mobility.id}
          onClose={closeUpload}
        />
      )}
    </Group>
  );
}
